import React from 'react';
import './AnafInvoicePreview.css';

// Format currency
const formatCurrency = (amount, currency = 'RON') => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('ro-RO', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(amount);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// Party Info Component
const PartyInfo = ({ title, party }) => {
  if (!party) return null;

  return (
    <div className="party-section">
      <h4>{title}</h4>
      <div className="party-details">
        <div className="party-name">{party.name || '-'}</div>
        <div className="party-cui">
          <span className="label">CUI:</span> {party.cui || party.party_id || '-'}
        </div>
        {party.reg_com && (
          <div className="party-reg">
            <span className="label">Reg. Com:</span> {party.reg_com}
          </div>
        )}
        <div className="party-address">
          {party.address && <div>{party.address}</div>}
          {(party.city || party.county) && (
            <div>
              {party.city}{party.city && party.county ? ', ' : ''}{party.county}
            </div>
          )}
          {party.country && party.country !== 'RO' && (
            <div>{party.country}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Invoice Lines Table
const InvoiceLinesTable = ({ lines, currency }) => {
  if (!lines || lines.length === 0) {
    return <div className="no-lines">Nu sunt linii de factura</div>;
  }

  return (
    <div className="lines-table-wrapper">
      <table className="lines-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Produs/Serviciu</th>
            <th className="align-right">Cant.</th>
            <th className="align-right">Pret</th>
            <th className="align-right">TVA</th>
            <th className="align-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={line.line_id || index}>
              <td className="line-num">{line.line_id || index + 1}</td>
              <td className="line-product">{line.product_name || '-'}</td>
              <td className="line-qty align-right">
                {line.quantity} {line.unit_code !== 'H87' ? line.unit_code : ''}
              </td>
              <td className="line-price align-right">
                {formatCurrency(line.unit_price, currency)}
              </td>
              <td className="line-vat align-right">
                {line.vat_category === 'O' ? 'N/A' : `${line.vat_rate}%`}
              </td>
              <td className="line-total align-right">
                {formatCurrency(line.line_net, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Totals Section
const TotalsSection = ({ totals, currency }) => {
  if (!totals) return null;

  return (
    <div className="totals-section">
      <div className="totals-grid">
        <div className="total-row">
          <span className="total-label">Subtotal (net):</span>
          <span className="total-value">
            {formatCurrency(totals.line_extension_amount || totals.tax_exclusive_amount, currency)}
          </span>
        </div>
        <div className="total-row">
          <span className="total-label">TVA:</span>
          <span className="total-value">
            {formatCurrency(totals.tax_amount, currency)}
          </span>
        </div>
        <div className="total-row total-grand">
          <span className="total-label">TOTAL:</span>
          <span className="total-value">
            {formatCurrency(totals.tax_inclusive_amount || totals.payable_amount, currency)}
          </span>
        </div>
      </div>
    </div>
  );
};

// VAT Breakdown
const VatBreakdown = ({ breakdown, currency }) => {
  if (!breakdown || breakdown.length === 0) return null;

  return (
    <div className="vat-breakdown">
      <h4>Detaliere TVA</h4>
      <div className="vat-rows">
        {breakdown.map((entry, index) => (
          <div key={index} className="vat-row">
            <span className="vat-category">{entry.category}</span>
            <span className="vat-rate">
              {entry.category === 'O' ? 'N/A' : `${entry.rate}%`}
            </span>
            <span className="vat-base">
              Baza: {formatCurrency(entry.taxable, currency)}
            </span>
            <span className="vat-amount">
              TVA: {formatCurrency(entry.tax, currency)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Modal Component
function AnafInvoicePreview({ isOpen, onClose, invoice, loading, error, messageId }) {
  if (!isOpen) return null;

  const currency = invoice?.header?.currency || invoice?.totals?.currency || 'RON';

  return (
    <div className="invoice-preview-overlay" onClick={onClose}>
      <div className="invoice-preview-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-title">
            <h2>Factura ANAF</h2>
            {messageId && <span className="msg-id">ID: {messageId}</span>}
          </div>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {loading && (
            <div className="modal-loading">
              <div className="loading-spinner"></div>
              <p>Se incarca factura...</p>
            </div>
          )}

          {error && (
            <div className="modal-error">
              <span className="error-icon">!</span>
              <p>{error}</p>
            </div>
          )}

          {invoice && !loading && !error && (
            <>
              {/* Invoice Header */}
              <div className="invoice-header-section">
                <div className="invoice-number">
                  <span className="label">Numar Factura:</span>
                  <span className="value">{invoice.header?.invoice_number || '-'}</span>
                </div>
                <div className="invoice-dates">
                  <div className="date-item">
                    <span className="label">Data Emitere:</span>
                    <span className="value">{formatDate(invoice.header?.issue_date)}</span>
                  </div>
                  {invoice.header?.due_date && (
                    <div className="date-item">
                      <span className="label">Scadenta:</span>
                      <span className="value">{formatDate(invoice.header?.due_date)}</span>
                    </div>
                  )}
                </div>
                <div className="invoice-currency">
                  <span className="label">Moneda:</span>
                  <span className="value">{currency}</span>
                </div>
              </div>

              {/* Parties */}
              <div className="parties-section">
                <PartyInfo title="Furnizor" party={invoice.seller} />
                <PartyInfo title="Cumparator" party={invoice.buyer} />
              </div>

              {/* Payment Info */}
              {invoice.payment && (
                <div className="payment-section">
                  <h4>Plata</h4>
                  <div className="payment-details">
                    <span className="payment-method">{invoice.payment.payment_method}</span>
                    {invoice.payment.iban && (
                      <span className="payment-iban">IBAN: {invoice.payment.iban}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Invoice Lines */}
              <div className="lines-section">
                <h4>Produse/Servicii</h4>
                <InvoiceLinesTable lines={invoice.lines} currency={currency} />
              </div>

              {/* Totals */}
              <TotalsSection totals={invoice.totals} currency={currency} />

              {/* VAT Breakdown */}
              <VatBreakdown breakdown={invoice.vat_breakdown} currency={currency} />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="close-modal-btn" onClick={onClose}>
            Inchide
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnafInvoicePreview;
